import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  CalendarDays,
  CheckSquare,
  Loader2,
  Mail,
  Paperclip,
  Phone,
  StickyNote,
  X,
} from 'lucide-react'
import clsx from 'clsx'
import type { Loan } from '@/types'
import { MAX_UPLOAD_BYTES, documentsService } from '@/services/documents'
import { loansService } from '@/services/loans'
import { LOAN_QUERY_KEYS, invalidateLoanQueries } from '@/services/loanQueryKeys'
import { NO_DEAL_EDIT, useCanEditDeal } from '@/components/deal/dossier/dealEditPermission'
import type { InspectorLogKind } from './types'

const ACTIONS: Array<{
  id: InspectorLogKind
  label: string
  Icon: typeof Phone
}> = [
  { id: 'call', label: 'Call', Icon: Phone },
  { id: 'email', label: 'Email', Icon: Mail },
  { id: 'meeting', label: 'Meeting', Icon: CalendarDays },
  { id: 'note', label: 'Note', Icon: StickyNote },
  { id: 'task', label: 'Task', Icon: CheckSquare },
]

const KIND_LABEL: Record<InspectorLogKind, string> = {
  call: 'Call',
  email: 'Email',
  meeting: 'Meeting',
  note: 'Note',
  task: 'Task',
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function toOutlookLocal(isoLocal: string): string {
  // datetime-local → Outlook deeplink expects YYYY-MM-DDTHH:mm:ss
  if (!isoLocal) return ''
  return isoLocal.length === 16 ? `${isoLocal}:00` : isoLocal
}

function toGoogleDates(startLocal: string, endLocal: string): string {
  const fmt = (value: string) => value.replace(/[-:]/g, '').replace('T', '') + '00'
  return `${fmt(startLocal)}/${fmt(endLocal)}`
}

function buildNoteBody(args: {
  kind: InspectorLogKind
  body: string
  scheduleStart: string
  scheduleEnd: string
  attendees: string
  uploadedNames: string[]
}): string {
  const lines = [`[${KIND_LABEL[args.kind]}]`, args.body.trim()]
  if (args.scheduleStart) {
    const end = args.scheduleEnd ? ` – ${args.scheduleEnd.replace('T', ' ')}` : ''
    lines.push(`Schedule: ${args.scheduleStart.replace('T', ' ')}${end}`)
  }
  if (args.attendees.trim()) {
    lines.push(`Attendees: ${args.attendees.trim()}`)
  }
  if (args.uploadedNames.length > 0) {
    lines.push(`Attachments: ${args.uploadedNames.join(', ')}`)
  }
  return lines.filter(Boolean).join('\n')
}

export function InspectorLogActionRow({
  active,
  onSelect,
}: {
  active: InspectorLogKind | null
  onSelect: (kind: InspectorLogKind) => void
}) {
  // Every log is saved as a note on the deal (POST /loans/{id}/notes:
  // pipeline:edit). A role without it is not offered a compose it cannot save.
  const canEditDeal = useCanEditDeal()
  return (
    <div className="grid grid-cols-5 gap-1 border-t border-amber-300/10 px-3 py-2">
      {ACTIONS.map(({ id, label, Icon }) => (
        <button
          key={id}
          type="button"
          onClick={() => {
            if (canEditDeal) onSelect(id)
          }}
          disabled={!canEditDeal}
          title={canEditDeal ? undefined : NO_DEAL_EDIT}
          className={clsx(
            'flex flex-col items-center gap-0.5 rounded border px-1 py-1.5 text-[9px] uppercase tracking-[0.12em] transition-colors',
            !canEditDeal
              ? 'cursor-not-allowed border-amber-300/10 text-stone-600 opacity-40'
              : active === id
                ? 'border-amber-300/40 bg-amber-300/15 text-amber-100'
                : 'border-amber-300/10 text-stone-400 hover:border-amber-300/25 hover:bg-amber-300/[0.06] hover:text-amber-100',
          )}
        >
          <Icon className="h-3.5 w-3.5 text-amber-300/90" />
          {label}
        </button>
      ))}
    </div>
  )
}

export function InspectorLogCompose({
  loan,
  kind,
  onClose,
  onSaved,
}: {
  loan: Pick<Loan, 'id' | 'property'>
  kind: InspectorLogKind
  onClose: () => void
  onSaved: () => void
}) {
  const queryClient = useQueryClient()
  // The row does not open this for a role without pipeline:edit; the Save is
  // held shut too, so no host that opens it another way can post a refused note.
  const canEditDeal = useCanEditDeal()
  const fileRef = useRef<HTMLInputElement>(null)
  const [body, setBody] = useState('')
  const [scheduleStart, setScheduleStart] = useState('')
  const [scheduleEnd, setScheduleEnd] = useState('')
  const [attendees, setAttendees] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setBody('')
    setScheduleStart('')
    setScheduleEnd('')
    setAttendees('')
    setFiles([])
    setError(null)
  }, [kind, loan.id])

  const save = useMutation({
    mutationFn: async () => {
      const uploadedNames: string[] = []
      for (const file of files) {
        if (file.size > MAX_UPLOAD_BYTES) {
          throw new Error(`${file.name} is ${humanSize(file.size)} — the limit is 50 MB.`)
        }
        await documentsService.upload(loan.id, file, {
          category: 'deal_log',
          notes: `${KIND_LABEL[kind]} log attachment`,
        })
        uploadedNames.push(file.name)
      }
      const noteBody = buildNoteBody({
        kind,
        body,
        scheduleStart,
        scheduleEnd,
        attendees,
        uploadedNames,
      })
      if (!noteBody.replace(`[${KIND_LABEL[kind]}]`, '').trim()) {
        throw new Error('Add a note, schedule, or attachment before saving.')
      }
      await loansService.addNote(loan.id, noteBody)
    },
    onSuccess: async () => {
      setError(null)
      await invalidateLoanQueries(queryClient, loan.id)
      await queryClient.invalidateQueries({ queryKey: ['loan-notes', loan.id] })
      await queryClient.invalidateQueries({ queryKey: ['loan', loan.id, 'documents'] })
      await queryClient.invalidateQueries({ queryKey: LOAN_QUERY_KEYS.activity(loan.id) })
      onSaved()
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } }; message?: string })
        ?.response?.data?.detail
      setError(typeof detail === 'string' ? detail : err instanceof Error ? err.message : 'Unable to save.')
    },
  })

  const dealLabel = loan.property || loan.id
  const showSchedule = kind === 'call' || kind === 'meeting'

  const launchEmail = () => {
    const subject = encodeURIComponent(`${KIND_LABEL[kind]} — ${dealLabel}`)
    const mailBody = encodeURIComponent(
      [body.trim(), scheduleStart ? `Proposed: ${scheduleStart.replace('T', ' ')}` : '']
        .filter(Boolean)
        .join('\n\n'),
    )
    window.open(`mailto:?subject=${subject}&body=${mailBody}`, '_blank')
  }

  const launchCalendar = (provider: 'outlook' | 'google') => {
    if (!scheduleStart || !scheduleEnd) {
      setError('Set start and end times before launching a calendar invite.')
      return
    }
    setError(null)
    const subject = `${KIND_LABEL[kind]} — ${dealLabel}`
    const details = [body.trim(), attendees.trim() ? `Attendees: ${attendees.trim()}` : '']
      .filter(Boolean)
      .join('\n')
    if (provider === 'outlook') {
      const params = new URLSearchParams({
        subject,
        startdt: toOutlookLocal(scheduleStart),
        enddt: toOutlookLocal(scheduleEnd),
        body: details,
      })
      if (attendees.trim()) params.set('to', attendees.trim())
      window.open(`https://outlook.office.com/calendar/0/deeplink/compose?${params}`, '_blank')
      return
    }
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: subject,
      dates: toGoogleDates(scheduleStart, scheduleEnd),
      details,
    })
    window.open(`https://calendar.google.com/calendar/render?${params}`, '_blank')
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-200/80">
            Log {KIND_LABEL[kind].toLowerCase()}
          </div>
          <div className="mt-0.5 text-[11px] text-stone-500">
            Writes to this deal’s note trail. Attachments upload to the deal file.
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-stone-500 hover:bg-white/5 hover:text-stone-200"
          aria-label="Close log form"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <label className="block">
        <span className="text-[9px] uppercase tracking-[0.14em] text-stone-600">Notes</span>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          placeholder={
            kind === 'task'
              ? 'What needs to be done, owner, and due expectation…'
              : `What happened on this ${KIND_LABEL[kind].toLowerCase()}…`
          }
          className="mt-1 w-full rounded border border-stone-700 bg-black/40 px-2.5 py-2 text-[12px] text-stone-100 outline-none focus:border-amber-300/40"
        />
      </label>

      {showSchedule ? (
        <div className="space-y-2 rounded-md border border-white/[0.04] bg-white/[0.015] p-2.5">
          <div className="text-[9px] uppercase tracking-[0.14em] text-stone-600">Schedule</div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[9px] text-stone-600">Start</span>
              <input
                type="datetime-local"
                value={scheduleStart}
                onChange={(e) => setScheduleStart(e.target.value)}
                className="mt-1 w-full rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[11px] text-stone-100 outline-none focus:border-amber-300/40"
              />
            </label>
            <label className="block">
              <span className="text-[9px] text-stone-600">End</span>
              <input
                type="datetime-local"
                value={scheduleEnd}
                onChange={(e) => setScheduleEnd(e.target.value)}
                className="mt-1 w-full rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[11px] text-stone-100 outline-none focus:border-amber-300/40"
              />
            </label>
          </div>
          <label className="block">
            <span className="text-[9px] text-stone-600">Attendees (emails, optional)</span>
            <input
              type="text"
              value={attendees}
              onChange={(e) => setAttendees(e.target.value)}
              placeholder="name@firm.com, …"
              className="mt-1 w-full rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[11px] text-stone-100 outline-none focus:border-amber-300/40"
            />
          </label>
          <div className="flex flex-wrap gap-1.5 pt-0.5">
            <button
              type="button"
              onClick={() => launchCalendar('outlook')}
              className="rounded border border-amber-300/25 px-2 py-1 text-[10px] text-amber-100 hover:bg-amber-300/10"
            >
              Launch Outlook
            </button>
            <button
              type="button"
              onClick={() => launchCalendar('google')}
              className="rounded border border-amber-300/25 px-2 py-1 text-[10px] text-amber-100 hover:bg-amber-300/10"
            >
              Launch Google Calendar
            </button>
          </div>
        </div>
      ) : null}

      {kind === 'email' ? (
        <button
          type="button"
          onClick={launchEmail}
          className="inline-flex items-center gap-1.5 rounded border border-amber-300/25 px-2.5 py-1.5 text-[10px] text-amber-100 hover:bg-amber-300/10"
        >
          <Mail className="h-3 w-3" /> Open mail draft
        </button>
      ) : null}

      <div className="rounded-md border border-white/[0.04] bg-white/[0.015] p-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[9px] uppercase tracking-[0.14em] text-stone-600">Attachments</div>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-amber-200 hover:bg-amber-300/10"
          >
            <Paperclip className="h-3 w-3" /> Add files
          </button>
          <input
            ref={fileRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              const next = Array.from(e.target.files ?? [])
              if (next.length) setFiles((current) => [...current, ...next])
              e.target.value = ''
            }}
          />
        </div>
        {files.length === 0 ? (
          <p className="mt-1.5 text-[11px] text-stone-500">No files selected.</p>
        ) : (
          <ul className="mt-1.5 space-y-1">
            {files.map((file) => (
              <li key={`${file.name}-${file.size}`} className="flex items-center gap-2 text-[11px] text-stone-300">
                <Paperclip className="h-3 w-3 shrink-0 text-stone-500" />
                <span className="min-w-0 flex-1 truncate">{file.name}</span>
                <span className="text-stone-600">{humanSize(file.size)}</span>
                <button
                  type="button"
                  onClick={() => setFiles((current) => current.filter((f) => f !== file))}
                  className="text-stone-500 hover:text-stone-200"
                  aria-label={`Remove ${file.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {error ? <p className="text-[11px] text-red-300">{error}</p> : null}

      <div className="flex gap-2">
        <button
          type="button"
          disabled={save.isPending || !canEditDeal}
          title={canEditDeal ? undefined : NO_DEAL_EDIT}
          onClick={() => {
            if (canEditDeal) save.mutate()
          }}
          className="inline-flex items-center gap-1.5 rounded bg-amber-300 px-3 py-1.5 text-[11px] font-medium text-black disabled:opacity-60"
        >
          {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {save.isPending ? 'Saving…' : `Save ${KIND_LABEL[kind].toLowerCase()}`}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-stone-600 px-3 py-1.5 text-[11px] text-stone-300"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
